import { useState, useCallback, useEffect } from "react";

const LS_ENABLED_KEY = "browser_notifications_enabled";
const LS_DISMISSED_KEY = "browser_notif_prompt_dismissed";

function supported() {
  return typeof Notification !== "undefined";
}

export function useBrowserNotifications() {
  const [permission, setPermission] = useState<NotificationPermission>(
    supported() ? Notification.permission : "denied"
  );
  const [enabled, setEnabled] = useState(() => {
    if (!supported()) return false;
    return localStorage.getItem(LS_ENABLED_KEY) !== "false";
  });
  const [promptDismissed, setPromptDismissed] = useState(() => {
    return localStorage.getItem(LS_DISMISSED_KEY) === "true";
  });

  useEffect(() => {
    if (!supported()) return;
    setPermission(Notification.permission);

    // Keep permission state in sync when user changes it via browser settings
    if (navigator.permissions) {
      navigator.permissions
        .query({ name: "notifications" as PermissionName })
        .then((status) => {
          status.onchange = () => setPermission(Notification.permission);
        })
        .catch(() => {});
    }

    // Sync enabled state across tabs / hook instances via storage events
    const onStorage = (e: StorageEvent) => {
      if (e.key === LS_ENABLED_KEY) {
        setEnabled(e.newValue !== "false");
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const requestPermission = useCallback(async () => {
    if (!supported()) return;
    const result = await Notification.requestPermission();
    setPermission(result);
    if (result === "granted") {
      localStorage.setItem(LS_ENABLED_KEY, "true");
      setEnabled(true);
    }
  }, []);

  const dismissPrompt = useCallback(() => {
    localStorage.setItem(LS_DISMISSED_KEY, "true");
    setPromptDismissed(true);
  }, []);

  const resetDismiss = useCallback(() => {
    localStorage.removeItem(LS_DISMISSED_KEY);
    setPromptDismissed(false);
  }, []);

  const toggle = useCallback(() => {
    setEnabled((prev) => {
      const next = !prev;
      localStorage.setItem(LS_ENABLED_KEY, String(next));
      return next;
    });
  }, []);

  // Reads Notification.permission live so all hook instances (Banner + Monitor)
  // immediately see the updated permission after the user clicks Enable —
  // no stale closure issue even though they are separate hook instances.
  const notify = useCallback(
    (title: string, options?: { body?: string; tag?: string; icon?: string }) => {
      if (!supported()) return;
      if (!enabled) return;
      if (Notification.permission !== "granted") return;
      try {
        const n = new Notification(title, {
          icon: "/favicon.ico",
          badge: "/favicon.ico",
          requireInteraction: true,
          ...options,
        });
        n.onclick = () => {
          window.focus();
          n.close();
        };
      } catch {}
    },
    [enabled]
  );

  const showPrompt = permission === "default" && !promptDismissed;

  return {
    permission,
    enabled,
    showPrompt,
    toggle,
    requestPermission,
    dismissPrompt,
    resetDismiss,
    notify,
  };
}
